import { useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"
import "./App.css"
import "./components/screens.css"
import { isSnowflake } from "@cookies/shared/snowflake"
import { BakeryStoreProvider } from "./game/bakeryStore"
import { useAuth } from "./auth/useAuth"
import { PrefsProvider, usePrefs } from "./contexts/PrefsContext"
import { resolveGuildId } from "./auth/session"
import { loginUrl } from "./auth/api"
import { navigate } from "./auth/router"
import { CallbackPage, LandingPage, ServersPage } from "./pages/AuthPages"
import AppChannelEntry from "./discord/AppChannelEntry"
import { BakeryShell, BakerySkeleton } from "./components/BakeryShell"
import { fxCssVars } from "./game/fxTiming"

function UserMenu({ username, onSignOut }: { username: string; onSignOut: () => void }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const el = wrapRef.current
      if (el !== null && e.target instanceof Node && !el.contains(e.target)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    window.addEventListener("pointerdown", onDown)
    window.addEventListener("keydown", onKey)
    return () => {
      window.removeEventListener("pointerdown", onDown)
      window.removeEventListener("keydown", onKey)
    }
  }, [open])
  return (
    <div className="bk-usermenu" ref={wrapRef}>
      <button type="button" className="bk-authbtn bk-userbtn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {username}
      </button>
      {open && (
        <div className="bk-usermenu-pop" role="menu">
          <button type="button" role="menuitem" className="bk-authbtn bk-signout" onClick={() => { setOpen(false); onSignOut() }}>
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

import { isAppChannelFrame } from "./utils/frame";

function AppInner() {
  const auth = useAuth()
  const prefs = usePrefs()
  const { reduced } = prefs
  const rawGuildId = auth.route.name === "game" ? auth.route.guildId : resolveGuildId()
  const cleanGuildId = typeof rawGuildId === "string" ? rawGuildId.trim() : ""
  const guildValid = cleanGuildId !== "" && isSnowflake(cleanGuildId)
  const guildId = guildValid ? cleanGuildId : null
  const canPlay = auth.discordMode ? auth.authed && guildValid : auth.route.name === "game" && auth.authed && guildValid
  const userId = auth.user?.id ?? ""

  const authControl = auth.authed ? (
    <UserMenu username={auth.user?.username ?? "Baker"} onSignOut={auth.logout} />
  ) : (
    <a className="bk-authbtn" href={loginUrl()} style={{ textDecoration: "none" }}>Login</a>
  )

  const authShell = (body: ReactNode) => (
    <div className={"bk-root" + (reduced ? " rm" : "")} style={fxCssVars()}>
      <header className="bk-header">
        <button type="button" className="bk-brand" onClick={() => navigate("/")} aria-label="Go home" title="Go home">
          <span className="bk-brand-ico" aria-hidden="true">🍪</span>
          <span className="bk-brand-text">
            <span className="bk-brand-name">Cookie Bakery</span>
            <span className="bk-brand-lv">Bakery</span>
          </span>
        </button>
        <div className="bk-self">{authControl}</div>
      </header>
      {body}
    </div>
  )

  if (!auth.ready && !auth.discordMode) {
    return <BakerySkeleton reduced={reduced} />
  }

  if (auth.discordMode && !canPlay) {
    if (auth.discordError) {
      return authShell(
        <main className="bk-main bk-sub">
          <div className="bk-wide">
            <h1>Could not join the bakery</h1>
            <p>{auth.discordError}</p>
            <button type="button" className="bk-authbtn" onClick={auth.retryDiscord}>
              Retry
            </button>
          </div>
        </main>,
      )
    }
    return <BakerySkeleton reduced={reduced} />
  }

  if (!auth.discordMode && auth.route.name === "callback") {
    return authShell(<CallbackPage onDone={auth.handleAuthDone} />)
  }

  if (!auth.discordMode && auth.route.name === "home") {
    if (auth.authed) {
      return authShell(
        <ServersPage token={auth.token} user={auth.user} onJoin={(id) => auth.goGame(id)} />,
      )
    }
    return authShell(<LandingPage />)
  }

  if (!auth.discordMode && auth.route.name === "game" && !auth.authed) {
    return authShell(<LandingPage />)
  }

  if (auth.route.name === "game" && !guildValid) {
    return authShell(
      <main className="bk-main bk-sub">
        <div className="bk-wide">
          <div style={{ fontSize: 48 }} aria-hidden="true">🍪</div>
          <h1>Invalid bakery link</h1>
          <p role="alert">This bakery link looks broken — pick another server.</p>
          <button type="button" className="bk-authbtn" onClick={() => navigate("/")}>
            Back to bakeries
          </button>
        </div>
      </main>,
    )
  }

  if (!canPlay || !guildId) {
    return <BakerySkeleton reduced={reduced} />
  }

  return (
    <BakeryStoreProvider guildId={guildId} token={auth.token} userId={userId} enabled={canPlay}>
      <BakeryShell />
    </BakeryStoreProvider>
  )
}

export default function App() {
  return (
    <PrefsProvider>
      {isAppChannelFrame() ? <AppChannelEntry /> : <AppInner />}
    </PrefsProvider>
  )
}
